/** Камера холста страниц: pan, zoom, сетка. Порт viewport.ts / ModelCanvas.tsx (ADR viewer). */

export const MIN_ZOOM = 0.15;
export const MAX_ZOOM = 2;
export const CAMERA_PAN_MIN_Y = 24;
export const PAN_STEP = 40;
export const PINCH_ZOOM_SENSITIVITY = 0.01;
export const WHEEL_ZOOM_SENSITIVITY = 0.001;
export const DISCRETE_WHEEL_DELTA = 40;
export const GRID_STEP = 18;
/** Стартовый масштаб холста страниц (кнопка сброса и первый кадр). */
export const PAGE_BASELINE_ZOOM = 0.5;

export const PAGE_FRAMES = [
  { width: 1800, label: "1800 · wide" },
  { width: 1440, label: "1440 · wide" },
  { width: 1024, label: "1024 · regular" },
  { width: 768, label: "768 · iPad" },
  { width: 390, label: "390 · mobile" },
];

export function wheelZoomFactor(deltaY, sensitivity) {
  return Math.exp(-deltaY * sensitivity);
}

export function isDiscreteWheelTick(deltaY) {
  return Math.abs(deltaY) >= DISCRETE_WHEEL_DELTA;
}

export function zoomWheelKind(event, modifierHeld = false) {
  const explicitModifier = modifierHeld || event.metaKey;
  if (explicitModifier) return "ctrl-wheel";
  if (!event.ctrlKey) return null;
  return isDiscreteWheelTick(event.deltaY) ? "ctrl-wheel" : "pinch";
}

export function clampZoom(value) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

export function snapPan(value) {
  return Math.round(value);
}

export function baselineViewport(clientWidth, clientHeight, graphWidth, graphHeight) {
  const zoom = PAGE_BASELINE_ZOOM;
  return {
    zoom,
    pan: {
      x: snapPan((clientWidth - graphWidth * zoom) / 2),
      y: snapPan(Math.max(CAMERA_PAN_MIN_Y, (clientHeight - graphHeight * zoom) / 6)),
    },
  };
}

export function zoomViewport(current, nextZoom, anchorX, anchorY) {
  const zoom = clampZoom(nextZoom);
  const ratio = zoom / current.zoom;
  return {
    zoom,
    pan: {
      x: snapPan(anchorX - (anchorX - current.pan.x) * ratio),
      y: snapPan(anchorY - (anchorY - current.pan.y) * ratio),
    },
  };
}

export function panViewport(current, dx, dy) {
  return {
    zoom: current.zoom,
    pan: {
      x: current.pan.x + dx,
      y: current.pan.y + dy,
    },
  };
}

function createDotGrid(svg, patternId) {
  svg.setAttribute("class", "page-canvas__grid");
  svg.setAttribute("aria-hidden", "true");
  const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
  const pattern = document.createElementNS("http://www.w3.org/2000/svg", "pattern");
  pattern.id = patternId;
  pattern.setAttribute("width", String(GRID_STEP));
  pattern.setAttribute("height", String(GRID_STEP));
  pattern.setAttribute("patternUnits", "userSpaceOnUse");
  const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  dot.setAttribute("cx", String(GRID_STEP / 2));
  dot.setAttribute("cy", String(GRID_STEP / 2));
  dot.setAttribute("r", "1");
  dot.classList.add("page-canvas__grid-dot");
  pattern.append(dot);
  defs.append(pattern);
  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  rect.setAttribute("width", "100%");
  rect.setAttribute("height", "100%");
  rect.setAttribute("fill", `url(#${patternId})`);
  svg.append(defs, rect);
}

function toolbarIconButton(ariaLabel, pressed = false) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "model-toolbar__btn";
  if (pressed) button.classList.add("model-toolbar__btn--active");
  button.setAttribute("aria-label", ariaLabel);
  return button;
}

const GRIP_ICON = `<svg class="model-toolbar__icon" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M4 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM4 7a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM4 12a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"/></svg>`;

const CURSOR_ICON = `<svg class="model-toolbar__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.3172 4.62596L15.8843 6.67702C16.654 6.96052 17.31 7.20218 17.7857 7.46219C18.2841 7.73459 18.7565 8.11787 18.8984 8.76409C19.0403 9.41032 18.772 9.95628 18.4337 10.4124C18.1108 10.8479 17.6164 11.3422 17.0364 11.9221L16.3224 12.6362L19.7466 16.0604C19.9366 16.2504 20.105 16.4188 20.2357 16.5686C20.3746 16.7279 20.5075 16.9062 20.5988 17.1266C20.8005 17.6136 20.8005 18.1608 20.5988 18.6479C20.5075 18.8682 20.3746 19.0466 20.2357 19.2058C20.105 19.3557 19.9366 19.5241 19.7466 19.714L19.714 19.7466C19.5241 19.9366 19.3557 20.105 19.2058 20.2357C19.0466 20.3746 18.8682 20.5075 18.6479 20.5988C18.1608 20.8005 17.6136 20.8005 17.1266 20.5988C16.9062 20.5075 16.7279 20.3746 16.5686 20.2357C16.4188 20.105 16.2504 19.9366 16.0604 19.7466L12.6362 16.3224L11.9221 17.0364C11.3422 17.6164 10.8479 18.1108 10.4124 18.4337C9.95629 18.772 9.41032 19.0403 8.76409 18.8984C8.11787 18.7565 7.73459 18.2841 7.46219 17.7857C7.20218 17.31 6.96052 16.654 6.67702 15.8843L4.62596 10.3172C4.04508 8.74059 3.57969 7.47746 3.37319 6.50443C3.16699 5.53281 3.15517 4.56717 3.86117 3.86117C4.56717 3.15517 5.53281 3.16699 6.50444 3.3732C7.47746 3.5797 8.74059 4.04508 10.3172 4.62596ZM6.19303 4.84052C5.32497 4.65629 5.05579 4.78787 4.92183 4.92183C4.78787 5.05579 4.65629 5.32497 4.84052 6.19303C5.02172 7.04685 5.44734 8.2077 6.05656 9.86128L8.06415 15.3105C8.37375 16.1508 8.57822 16.7001 8.7784 17.0663C8.96614 17.4097 9.06401 17.429 9.08413 17.433C9.10755 17.4386 9.20448 17.4621 9.51887 17.2289C9.85409 16.9803 10.2699 16.5673 10.9032 15.934L12.1058 14.7314C12.3987 14.4385 12.8736 14.4385 13.1665 14.7314L17.1048 18.6697C17.3159 18.8808 17.448 19.0123 17.5546 19.1053C17.6562 19.1939 17.6935 19.21 17.7006 19.213C17.8201 19.2625 17.9544 19.2625 18.0739 19.213C18.081 19.21 18.1183 19.1939 18.2198 19.1053C18.3264 19.0123 18.4585 18.8808 18.6697 18.6697C18.8808 18.4585 19.0123 18.3264 19.1053 18.2198C19.1939 18.1183 19.21 18.081 19.213 18.0739C19.2625 17.9544 19.2625 17.8201 19.213 17.7006C19.21 17.6935 19.1939 17.6562 19.1053 17.5546C19.0123 17.448 18.8808 17.3159 18.6697 17.1048L14.7314 13.1665C14.4385 12.8736 14.4385 12.3987 14.7314 12.1058L15.934 10.9032C16.5673 10.2699 16.9803 9.85409 17.2289 9.51887C17.4621 9.20448 17.4386 9.10755 17.4337 9.08762C17.429 9.06401 17.4097 8.96614 17.0663 8.7784C16.7001 8.57822 16.1508 8.37375 15.3105 8.06415L9.86128 6.05656C8.2077 5.44734 7.04685 5.02172 6.19303 4.84052Z" fill="currentColor"/></svg>`;

export function createPageCanvas() {
  const root = document.createElement("div");
  root.className = "page-canvas";

  const toolbar = document.createElement("div");
  toolbar.className = "model-toolbars";

  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "model-toolbar model-toolbar--solo";
  resetBtn.textContent = `${Math.round(PAGE_BASELINE_ZOOM * 100)}%`;
  resetBtn.setAttribute("aria-label", "Сбросить масштаб и позицию");

  const zoomOutBtn = toolbarIconButton("Уменьшить");
  zoomOutBtn.textContent = "−";
  const zoomLabel = document.createElement("span");
  zoomLabel.className = "model-toolbar__zoom";
  zoomLabel.textContent = `${Math.round(PAGE_BASELINE_ZOOM * 100)}%`;
  const zoomInBtn = toolbarIconButton("Увеличить");
  zoomInBtn.textContent = "+";

  const zoomGroup = document.createElement("div");
  zoomGroup.className = "model-toolbar";
  zoomGroup.setAttribute("role", "toolbar");
  zoomGroup.setAttribute("aria-label", "Масштаб");
  zoomGroup.append(zoomOutBtn, zoomLabel, zoomInBtn);

  const selectBtn = toolbarIconButton("Курсор", true);
  selectBtn.setAttribute("aria-pressed", "true");
  selectBtn.innerHTML = CURSOR_ICON;

  const panBtn = toolbarIconButton("Перетаскивание");
  panBtn.setAttribute("aria-pressed", "false");
  panBtn.innerHTML = GRIP_ICON;

  const modeGroup = document.createElement("div");
  modeGroup.className = "model-toolbar";
  modeGroup.setAttribute("role", "toolbar");
  modeGroup.setAttribute("aria-label", "Режим мыши");
  modeGroup.append(selectBtn, panBtn);

  toolbar.append(resetBtn, zoomGroup, modeGroup);

  const pageSelectWrap = document.getElementById("page-select-wrap");
  if (pageSelectWrap) {
    toolbar.insertBefore(pageSelectWrap, toolbar.firstChild);
  }

  const viewport = document.createElement("div");
  viewport.className = "page-canvas__viewport page-canvas__viewport--select";
  viewport.tabIndex = 0;
  viewport.setAttribute(
    "aria-label",
    "Холст страниц. Колесо — сдвиг, Shift+колесо — горизонтально, Ctrl+колесо — масштаб.",
  );

  const gridId = `page-grid-${Math.random().toString(36).slice(2, 9)}`;
  const grid = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  createDotGrid(grid, gridId);

  const spriteHost = document.createElement("div");
  spriteHost.className = "page-canvas__sprite-host";

  const world = document.createElement("div");
  world.className = "page-canvas__world";

  const row = document.createElement("div");
  row.className = "page-canvas__frames";
  world.append(row);

  viewport.append(grid, spriteHost, world);
  root.append(toolbar, viewport);

  let pointerMode = "select";
  let panning = false;
  let viewportState = { zoom: PAGE_BASELINE_ZOOM, pan: { x: 0, y: 0 } };
  let drag = null;
  let zoomModifierHeld = false;
  let graphSize = { width: 0, height: 0 };

  function applyTransform() {
    world.style.transform = `translate(${viewportState.pan.x}px, ${viewportState.pan.y}px) scale(${viewportState.zoom})`;
    zoomLabel.textContent = `${Math.round(viewportState.zoom * 100)}%`;
  }

  function commitViewport(next) {
    viewportState = next;
    applyTransform();
  }

  function measureGraph() {
    graphSize = {
      width: row.scrollWidth,
      height: row.scrollHeight,
    };
  }

  function resetToBaseline() {
    measureGraph();
    const rect = viewport.getBoundingClientRect();
    commitViewport(baselineViewport(rect.width, rect.height, graphSize.width, graphSize.height));
  }

  function zoomAt(factor, anchorX, anchorY) {
    commitViewport(zoomViewport(viewportState, viewportState.zoom * factor, anchorX, anchorY));
  }

  function zoomBy(delta) {
    const rect = viewport.getBoundingClientRect();
    zoomAt(1 + delta, rect.width / 2, rect.height / 2);
  }

  function setPointerMode(mode) {
    pointerMode = mode;
    const selectActive = mode === "select" && !panning;
    const panActive = mode === "pan" || panning;
    selectBtn.classList.toggle("model-toolbar__btn--active", selectActive);
    selectBtn.setAttribute("aria-pressed", String(selectActive));
    panBtn.classList.toggle("model-toolbar__btn--active", panActive);
    panBtn.setAttribute("aria-pressed", String(panActive));
    viewport.classList.toggle("page-canvas__viewport--select", mode === "select" && !panning);
    viewport.classList.toggle("page-canvas__viewport--pan", mode === "pan" || panning);
  }

  function beginPan(event) {
    drag = {
      x: event.clientX,
      y: event.clientY,
      panX: viewportState.pan.x,
      panY: viewportState.pan.y,
    };
    panning = true;
    viewport.classList.add("page-canvas__viewport--grabbing");
    setPointerMode(pointerMode);
    viewport.setPointerCapture(event.pointerId);
  }

  function endPan(event) {
    if (!drag) return;
    drag = null;
    panning = false;
    viewport.classList.remove("page-canvas__viewport--grabbing");
    setPointerMode(pointerMode);
    if (event.pointerId != null && viewport.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }
  }

  resetBtn.addEventListener("click", resetToBaseline);
  zoomOutBtn.addEventListener("click", () => zoomBy(-0.1));
  zoomInBtn.addEventListener("click", () => zoomBy(0.1));
  selectBtn.addEventListener("click", () => setPointerMode("select"));
  panBtn.addEventListener("click", () => setPointerMode("pan"));

  viewport.addEventListener("wheel", (event) => {
    event.preventDefault();
    const rect = viewport.getBoundingClientRect();
    const zoomKind = zoomWheelKind(event, zoomModifierHeld);
    if (zoomKind) {
      zoomAt(
        wheelZoomFactor(
          event.deltaY,
          zoomKind === "pinch" ? PINCH_ZOOM_SENSITIVITY : WHEEL_ZOOM_SENSITIVITY,
        ),
        event.clientX - rect.left,
        event.clientY - rect.top,
      );
      return;
    }
    if (event.shiftKey) {
      commitViewport(panViewport(viewportState, -event.deltaY, 0));
      return;
    }
    commitViewport(panViewport(viewportState, -event.deltaX, -event.deltaY));
  }, { passive: false });

  viewport.addEventListener("pointerdown", (event) => {
    const panWithLeft = event.button === 0 && pointerMode === "pan";
    const panWithRight = event.button === 2;
    if (!panWithLeft && !panWithRight) return;
    event.preventDefault();
    beginPan(event);
  });

  viewport.addEventListener("pointermove", (event) => {
    if (!drag) return;
    commitViewport({
      zoom: viewportState.zoom,
      pan: {
        x: drag.panX + (event.clientX - drag.x),
        y: drag.panY + (event.clientY - drag.y),
      },
    });
  });

  viewport.addEventListener("pointerup", endPan);
  viewport.addEventListener("pointercancel", endPan);
  viewport.addEventListener("contextmenu", (event) => event.preventDefault());

  viewport.addEventListener("keydown", (event) => {
    if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoomBy(0.1);
      return;
    }
    if (event.key === "-" || event.key === "_") {
      event.preventDefault();
      zoomBy(-0.1);
      return;
    }
    if (event.key === "0") {
      event.preventDefault();
      resetToBaseline();
      return;
    }
    const arrows = {
      ArrowLeft: { dx: PAN_STEP, dy: 0 },
      ArrowRight: { dx: -PAN_STEP, dy: 0 },
      ArrowUp: { dx: 0, dy: PAN_STEP },
      ArrowDown: { dx: 0, dy: -PAN_STEP },
    };
    const move = arrows[event.key];
    if (!move) return;
    event.preventDefault();
    commitViewport(panViewport(viewportState, move.dx, move.dy));
  });

  window.addEventListener("keydown", (event) => {
    if (event.key === "Control" || event.key === "Meta") zoomModifierHeld = true;
  }, true);
  window.addEventListener("keyup", (event) => {
    if ((event.key === "Control" || event.key === "Meta") && !event.ctrlKey && !event.metaKey) {
      zoomModifierHeld = false;
    }
  }, true);
  window.addEventListener("blur", () => { zoomModifierHeld = false; });

  return {
    root,
    row,
    spriteHost,
    resetToBaseline,
    toolbars: toolbar,
  };
}

export function buildFrameShell(frameSpec, bodyEl) {
  const frame = document.createElement("article");
  frame.className = "hv-page-frame";
  frame.dataset.width = String(frameSpec.width);
  frame.style.width = `${frameSpec.width}px`;

  const label = document.createElement("div");
  label.className = "hv-page-frame__label";
  label.textContent = frameSpec.label;

  const screen = document.createElement("div");
  screen.className = "hv-page-frame__screen";
  screen.append(bodyEl);

  frame.append(label, screen);
  return frame;
}
